'use client';

import { useTransition, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Crown, 
  CreditCard,
  Zap,
  AlertTriangle,
  Loader2,
  User,
  Unlock,
  RefreshCw
} from 'lucide-react';
import { purchaseCreditAction } from '@/app/actions/payment-actions';
import { unlockReviewWithSubscription, unlockReviewWithCredit } from '@/app/actions/review-access-actions';
import { createPlanManagementUrl, getCurrentReviewUrl } from '@/lib/utils/secure-url-validation';
import { toast } from 'sonner';

interface UserPlan {
  subscription_plan: string;
  reviews_remaining: number;
  plan_title?: string;
  subscription_status?: string;
}

interface PaywallGateClientProps {
  reviewTitle: string;
  reviewId: string;
  userType: 'tenant';
  isAuthenticated?: boolean;
  userId?: string | null;
  subscriptionActive?: boolean;
  reviewsRemaining?: number;
  userPlan?: UserPlan | null; // ✅ Receive pre-fetched data as prop
}

export default function PaywallGateClient({ 
  reviewTitle, 
  reviewId, 
  userType,
  isAuthenticated = false,
  userId,
  subscriptionActive,
  reviewsRemaining,
  userPlan
}: PaywallGateClientProps) {
  const [isPending, startTransition] = useTransition();
  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false);
  const [isUnlockLoading, setIsUnlockLoading] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const router = useRouter();

  const isNetworkError = (error: string) => {
    return error.toLowerCase().includes('network') || 
           error.toLowerCase().includes('fetch') ||
           error.toLowerCase().includes('connection') ||
           error.toLowerCase().includes('timeout') ||
           error.toLowerCase().includes('offline');
  };

  const handleWithTimeout = async <T,>(
    operation: () => Promise<T>,
    timeoutMs: number = 15000
  ): Promise<T> => {
    return Promise.race([
      operation(),
      new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Operation timed out')), timeoutMs)
      )
    ]);
  };

  const clearErrors = () => {
    setOperationError(null);
    setRetryAttempts(0);
  };

  // ✅ No useState + useEffect - data comes pre-fetched from Server Component

  // Handle case where user data couldn't be fetched
  if (!isAuthenticated) {
    return (
      <Card className="border-2 border-red-200 shadow-xl backdrop-blur-sm bg-red-50/95">
        <CardContent className="py-8 text-center">
          <p className="text-red-600">Please log in to access this review.</p>
          <Button onClick={() => router.push('/login')} className="mt-4">
            Log In
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!userPlan) {
    return (
      <Card className="border-2 border-yellow-200 shadow-xl backdrop-blur-sm bg-yellow-50/95">
        <CardContent className="py-8 text-center">
          <p className="text-yellow-600">Unable to load your subscription information.</p>
          <Button onClick={() => router.refresh()} className="mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleUpgradePlan = () => {
    if (isUpgradeLoading) return;
    
    setIsUpgradeLoading(true);
    clearErrors();
    
    try {
      // Get current review page URL and create secure plan management URL
      const currentUrl = getCurrentReviewUrl();
      const planManagementUrl = createPlanManagementUrl(currentUrl);
      
      // Navigate to plan management page
      router.push(planManagementUrl);
    } catch (error) {
      console.error('Navigation error:', error);
      setIsUpgradeLoading(false);
      setOperationError("Unable to navigate to plan management. Please try again.");
    }
    // Note: Don't set loading to false here as navigation will unmount the component
  };

  const handleBuyOneAccess = async () => {
    if (!userId) {
      router.push('/login');
      return;
    }

    clearErrors();

    startTransition(async () => {
      try {
        const formData = new FormData();
        formData.append('userId', userId);
        
        const result = await handleWithTimeout(
          () => purchaseCreditAction(null, formData),
          20000
        );
        
        if (result.success && result.data?.authorizationUrl) {
          // Redirect to Paystack payment
          window.location.href = result.data.authorizationUrl;
        } else {
          const errorMessage = result.error || "Failed to initialize payment";
          setOperationError(errorMessage);
          toast.error(errorMessage);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to process payment";
        setOperationError(errorMessage);
        toast.error(errorMessage);
      }
    });
  };

  const handleUnlockReview = async () => {
    if (!userId) {
      router.push('/login');
      return;
    }

    if (isUnlockLoading) return;

    setIsUnlockLoading(true);
    clearErrors();

    try {
      const result = await handleWithTimeout(
        () => unlockReviewWithSubscription(reviewId),
        15000
      );
      
      if (result.success) {
        toast.success(result.message || "Review unlocked successfully!");
        router.refresh();
      } else {
        const errorMessage = result.error || "Failed to unlock review";
        setOperationError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to unlock review";
      setOperationError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUnlockLoading(false);
    }
  };

  const handleUnlockWithCredit = async () => {
    if (!userId) {
      router.push('/login');
      return;
    }

    if (isUnlockLoading) return;

    setIsUnlockLoading(true);
    clearErrors();

    try {
      const result = await handleWithTimeout(
        () => unlockReviewWithCredit(reviewId),
        15000
      );
      
      if (result.success) {
        toast.success(result.message || "Review unlocked with credit!");
        router.refresh();
      } else {
        const errorMessage = result.error || "Failed to unlock review with credit";
        setOperationError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to unlock review";
      setOperationError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUnlockLoading(false);
    }
  };

  const handleRetryOperation = async () => {
    if (retryAttempts >= 3) return;
    
    setRetryAttempts(prev => prev + 1);
    clearErrors();
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (operationError?.includes('unlock')) {
      if (currentPlan === 'pay-as-you-go') {
        await handleUnlockWithCredit();
      } else {
        await handleUnlockReview();
      }
    } else if (operationError?.includes('payment')) {
      await handleBuyOneAccess();
    }
  };

  // ✅ No loading state needed - data is pre-fetched server-side

  // Get current plan info - handle missing data gracefully
  const currentPlan = userPlan?.subscription_plan;
  const remainingReviews = userPlan?.reviews_remaining ?? reviewsRemaining ?? 0;
  const planTitle = userPlan?.plan_title;

  // Check if user has exhausted their reviews
  const hasNoReviews = remainingReviews === 0;
  const showRetryOption = operationError && isNetworkError(operationError) && retryAttempts < 3;

  return (
    <Card className="border-2 border-purple-200 shadow-xl backdrop-blur-sm" style={{ background: 'linear-gradient(to bottom right, rgba(250, 245, 255, 0.95), rgba(253, 244, 255, 0.95))' }}>
      <CardHeader className="text-center">
        <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
          <Crown className="h-8 w-8 text-purple-600" />
        </div>
        <CardTitle className="text-2xl">Review Access Required</CardTitle>
        <CardDescription className="text-lg">
          Unlock access to "{reviewTitle}"
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Plan Display */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <User className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-sm text-blue-800">Your Current Plan</p>
              <p className="text-sm text-blue-700">
                {planTitle || (currentPlan ? `${currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1).replace('-', ' ')}` : 'Plan information unavailable')}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Reviews remaining: {remainingReviews}
              </p>
            </div>
          </div>
        </div>

        {hasNoReviews ? (
          <div className="space-y-4">
            {/* No reviews remaining message */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-amber-800">
                    {currentPlan === 'pay-as-you-go' 
                      ? 'No Reviews Remaining' 
                      : 'Reviews Allocation Exhausted'
                    }
                  </p>
                  <p className="text-xs text-amber-700">
                    {currentPlan === 'pay-as-you-go'
                      ? 'Purchase individual review access or upgrade to a subscription plan.'
                      : 'You\'ve used all reviews in your current plan. Upgrade or switch to Pay-as-You-Go.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Action Options */}
            <div className="space-y-3">
              {currentPlan === 'pay-as-you-go' ? (
                <>
                  {/* Pay-as-you-go specific options */}
                  <Button 
                    onClick={handleBuyOneAccess}
                    disabled={isPending}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Buy One Access - ₦3,999
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={handleUpgradePlan}
                    disabled={isUpgradeLoading}
                    className="w-full"
                    size="lg"
                  >
                    {isUpgradeLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading Plan Management...
                      </>
                    ) : (
                      <>
                        <Crown className="h-4 w-4 mr-2" />
                        Upgrade Your Plan
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  {/* Other plans - upgrade option only */}
                  <Button 
                    onClick={handleUpgradePlan}
                    disabled={isUpgradeLoading}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    {isUpgradeLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading Plan Management...
                      </>
                    ) : (
                      <>
                        <Crown className="h-4 w-4 mr-2" />
                        Upgrade Your Plan
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={handleUpgradePlan}
                    disabled={isUpgradeLoading}
                    className="w-full"
                    size="lg"
                  >
                    {isUpgradeLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading Plan Management...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Switch to Pay-as-You-Go
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          /* User has reviews remaining - show unlock button */
          <div className="space-y-4">
            {/* Reviews available message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Unlock className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-green-800">Ready to Unlock</p>
                  <p className="text-xs text-green-700">
                    Use one of your {remainingReviews} remaining review{remainingReviews === 1 ? '' : 's'} to access this content.
                  </p>
                </div>
              </div>
            </div>

            {/* Unlock button */}
            <Button 
              onClick={currentPlan === 'pay-as-you-go' ? handleUnlockWithCredit : handleUnlockReview}
              disabled={isUnlockLoading}
              className="w-full bg-green-600 hover:bg-green-700"
              size="lg"
            >
              {isUnlockLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Unlocking Review...
                </>
              ) : (
                <>
                  <Unlock className="h-4 w-4 mr-2" />
                  {currentPlan === 'pay-as-you-go' 
                    ? `Unlock with Credit (${remainingReviews} available)`
                    : `Unlock Review (${remainingReviews} remaining)`
                  }
                </>
              )}
            </Button>

            {/* Alternative options */}
            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-600 mb-3 text-center">
                Or choose another option:
              </p>
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  onClick={handleUpgradePlan}
                  disabled={isUpgradeLoading}
                  className="w-full"
                  size="sm"
                >
                  {isUpgradeLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading Plan Management...
                    </>
                  ) : (
                    <>
                      <Crown className="h-4 w-4 mr-2" />
                      Manage Your Plan
                    </>
                  )}
                </Button>
                {currentPlan === 'pay-as-you-go' && (
                  <Button 
                    variant="outline" 
                    onClick={handleBuyOneAccess}
                    disabled={isPending}
                    className="w-full"
                    size="sm"
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Buy One-Time Access - ₦3,999
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <Separator />

        {operationError && (
          <div className="mx-6 mb-4">
            <div className={`border rounded-lg p-4 ${
              isNetworkError(operationError) 
                ? 'bg-yellow-50 border-yellow-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`text-sm font-medium ${
                isNetworkError(operationError) 
                  ? 'text-yellow-800' 
                  : 'text-red-800'
              }`}>
                {isNetworkError(operationError) 
                  ? 'Connection Issue: ' 
                  : 'Error: '
                }{operationError}
              </p>
              {isNetworkError(operationError) && (
                <p className="text-yellow-700 text-xs mt-1">
                  Please check your internet connection and try again.
                </p>
              )}
              {showRetryOption && (
                <Button
                  onClick={handleRetryOperation}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Retry ({3 - retryAttempts} attempts left)
                </Button>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center">
          Need help choosing? Visit our pricing page for detailed plan comparisons.
        </p>
      </CardContent>
    </Card>
  );
} 